(() => {
  'use strict';
  const RENDERER = 'https://tuvd1ov9o6.execute-api.us-west-1.amazonaws.com/customizer/customizer-prod';
  const flag = value => value === true || value === 1 || /^(yes|true|1)$/i.test(String(value));
  const unwrap = value => value && typeof value === 'object' && 'value' in value ? value.value : value;
  function normalize(config) {
    config.fields = Number(unwrap(config.fields));
    config.limits = String(unwrap(config.limits) || '').split('__').map(Number);
    config.caps = unwrap(config.caps) == null ? true : flag(unwrap(config.caps));
    config.lower = flag(unwrap(config.lower));
    config.settings = Object.fromEntries(Object.entries(config.settings || {}).map(([key, value]) => [key, unwrap(value)]));
    return config;
  }
  const textFor = (value, config) => config.lower ? value.toLowerCase() : config.caps ? value.toUpperCase() : value;
  function variantFor(config, personalized) {
    const variants = config.product.variants || [];
    const exact = variants.find(v => v.title === (personalized ? 'Add Custom Text' : 'Leave Blank'));
    if (exact) return exact;
    if ((config.product.tags || []).includes('v2')) return variants.find(v => v.available) || variants[0];
    return null;
  }
  function priceFor(config, personalized) {
    const variant = variantFor(config, personalized);
    return variant ? Number(variant.price) : 0;
  }
  function makeItem(config, lines, quantity) {
    if (lines.some((line,index) => config.limits[index] && line.length > config.limits[index])) throw new Error('Your text is longer than the limit for this bag. Please shorten it before adding to cart.');
    const personalized = lines.some(line => line.trim());
    const variant = variantFor(config, personalized);
    if (!variant || !variant.available) throw new Error(`${config.product.title}: this option is sold out. Please edit your group.`);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) throw new Error('Choose a quantity between 1 and 100 for each bag.');
    const properties = {source: 'bulk_customizer'};
    if (personalized) properties.customization = 'Your text: ' + lines.join(' | ');
    if (config.product.tags.includes('v2')) properties.customized = personalized ? 'yes' : 'no';
    return {id: variant.id, quantity, properties};
  }
  function tagVariant(config) {
    return config.product.variants.filter(v => v.title !== 'Add Custom Text').at(-1) || null;
  }
  function tagItem(parent, config, text, quantity) {
    const variant=tagVariant(config);
    if (!variant?.available) throw new Error('The leather tag is sold out. Please remove it or choose another bag.');
    const properties={source:'bulk_customizer',customization:'Your text: '+text};
    if(parent.product.tags.includes('v2')) properties.customized='yes';
    return {id:variant.id,quantity,properties};
  }
  function itemsFor(config, rows) {
    if (!rows.length) throw new Error('Add at least one bag.');
    return rows.flatMap(row => {
      const items = [makeItem(config, row.lines, row.qty)];
      if (row.tag !== false && row.tag.trim()) {
        if (!config.giftTag) throw new Error('The leather tag is unavailable.');
        items.push(tagItem(config, config.giftTag, row.tag, row.qty));
      }
      return items;
    });
  }
  function previewURL(config, lines, isTag = false) {
    const url = new URL(RENDERER);
    Object.entries(config.settings || {}).forEach(([key, value]) => {
      if (key !== 'is_para' && value != null) url.searchParams.set(key, String(value));
    });
    if (isTag) url.searchParams.set('is_para', '1');
    const bytes = new TextEncoder().encode(lines.join('__'));
    url.searchParams.set('words', btoa(Array.from(bytes, x => String.fromCharCode(x)).join('')));
    return url.href;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {normalize, variantFor, priceFor, makeItem, itemsFor, previewURL, textFor, tagVariant, tagItem};
  if (typeof customElements === 'undefined' || customElements.get('apolis-group-customizer')) return;
  class GroupCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true; this.rows = []; this.active = 0; this.previewRevision = 0; this.busy = false;
      this.root = this.dataset.root || '/';
      this.status = this.querySelector('[data-status]');
      this.form = this.querySelector('[data-form]');
      this.dialog = this.querySelector('[data-confirm]');
      this.money = value => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency', currency:this.dataset.currency || 'USD'}).format(value / 100);
      this.querySelector('[data-add-row]').addEventListener('click', () => { this.addRow(); this.render(); this.preview(); this.querySelector('[data-rows]').lastElementChild.querySelector('input').focus(); });
      this.form.addEventListener('submit', e => { e.preventDefault(); this.confirm(); });
      this.querySelector('[data-edit]').addEventListener('click', () => this.dialog.close());
      this.querySelector('[data-confirm-add]').addEventListener('click', () => this.addToCart());
      this.form.addEventListener('input', e => this.input(e));
      this.form.addEventListener('focusin', e => {
        if (e.target.dataset.row === undefined) return;
        this.active = Number(e.target.dataset.row); this.activeTag = e.target.dataset.kind === 'tag'; this.preview();
      });
      this.load();
    }
    error(message) { this.status.hidden = false; this.status.textContent = message; }
    async load() {
      const handle = new URL(location.href).searchParams.get('product_handle');
      if (!handle || !/^[a-z0-9-]+$/.test(handle)) { this.error('Choose a bag from Group Gifts to start your group order.'); return; }
      try {
        const response = await fetch(this.root + 'products/' + encodeURIComponent(handle) + '?view=apolis-group-data', {credentials:'same-origin'});
        if (!response.ok) throw new Error('This bag could not be loaded. Please return to Group Gifts and choose an available bag.');
        this.config = normalize(await response.json());
        if (this.config.giftTag) this.config.giftTag = normalize(this.config.giftTag);
        if (!this.config.product || this.config.fields < 1 || this.config.fields > 3 || !Object.keys(this.config.settings).length || this.config.limits.slice(0,this.config.fields).some(n => !Number.isInteger(n) || n < 1) || this.config.limits.length < this.config.fields) throw new Error('Group customization is unavailable for this bag. Please choose another bag from Group Gifts.');
        if (!variantFor(this.config, true)?.available && !variantFor(this.config, false)?.available) throw new Error('This bag is sold out. Please choose another bag from Group Gifts.');
        this.querySelector('[data-product-title]').textContent = 'Personalize the ' + this.config.product.title;
        this.addRow(); this.addRow(); this.active = 0;
        this.render(); this.status.hidden = true; this.querySelector('[data-editor]').hidden = false; this.preview();
      } catch (error) { this.error(error.message || 'Unable to load this bag. Please try again.'); }
    }
    addRow() { this.rows.push({lines:Array(this.config.fields).fill(''),qty:1,tag:false}); this.active = this.rows.length - 1; this.activeTag = false; }
    element(tag, text, attrs = {}) {
      const el = document.createElement(tag); if (text != null) el.textContent = text;
      Object.entries(attrs).forEach(([key,value]) => el.setAttribute(key, value)); return el;
    }
    render() {
      const list = this.querySelector('[data-rows]'); list.replaceChildren();
      this.rows.forEach((row, index) => {
        const fieldset = this.element('fieldset'); fieldset.append(this.element('legend', `Bag ${index + 1}`));
        const fields = this.element('div', null, {class:'apolis-group__fields',style:`--group-fields:${this.config.fields}`});
        row.lines.forEach((line, lineIndex) => {
          const label = this.element('label', `Line ${lineIndex + 1}`);
          const input = this.element('input', null, {'data-row':index,'data-line':lineIndex,maxlength:this.config.limits[lineIndex],autocomplete:'off','aria-label':`Bag ${index + 1}, line ${lineIndex + 1}`}); input.value = line; label.append(input); fields.append(label);
        }); fieldset.append(fields);
        const footer = this.element('div', null, {class:'apolis-group__row-footer'});
        const qtyLabel = this.element('label','Quantity'); const qty = this.element('input',null,{type:'number',min:1,max:100,step:1,required:'','data-row':index,'data-kind':'quantity','aria-label':`Bag ${index + 1}, quantity`});qty.value=row.qty;qtyLabel.append(qty);footer.append(qtyLabel);
        footer.append(this.element('p',null,{'data-row-total':index}));
        const remove = this.element('button','Remove',{type:'button','aria-label':`Remove bag ${index + 1}`});remove.addEventListener('click',()=>{this.rows.splice(index,1);this.active=Math.max(0,Math.min(index,this.rows.length-1));this.activeTag=false;this.render();this.preview();(this.querySelector(`[data-row="${this.active}"]`) || this.querySelector('[data-add-row]')).focus();});footer.append(remove);
        const tagConfig = this.config.giftTag;
        if (tagConfig && tagVariant(tagConfig)?.available) {
          const tag = this.element('button',row.tag === false ? 'Add a leather tag' : 'Remove leather tag',{type:'button'});
          tag.addEventListener('click',()=>{row.tag=row.tag === false ? '' : false;this.active=index;this.activeTag=row.tag!==false;this.render();this.preview();this.querySelector(`[data-row="${index}"][data-kind="${row.tag===false?'quantity':'tag'}"]`).focus();});footer.append(tag);
          if (row.tag !== false) {
            const label = this.element('label','Leather tag text',{class:'apolis-group__tag'});
            const input = this.element('textarea',null,{'data-row':index,'data-kind':'tag',maxlength:240,'aria-label':`Bag ${index + 1}, leather tag text`});input.value=row.tag;label.append(input);fieldset.append(label);
          }
        }
        fieldset.append(footer);list.append(fieldset);
      }); this.totals();
    }
    input(e) {
      const el = e.target; if (el.dataset.row === undefined) return;
      const index=Number(el.dataset.row); const row=this.rows[index]; if (!row) return;
      if (el.dataset.kind === 'quantity') row.qty=Number(el.value);
      else if (el.dataset.kind === 'tag') {row.tag=textFor(el.value,this.config.giftTag);el.value=row.tag;}
      else {row.lines[Number(el.dataset.line)]=textFor(el.value,this.config);el.value=row.lines[Number(el.dataset.line)];}
      this.active=index;this.activeTag=el.dataset.kind==='tag';this.totals();
      if (el.dataset.kind !== 'quantity') {clearTimeout(this.previewTimer);this.previewTimer=setTimeout(()=>this.preview(),250);}
    }
    totals() {
      let total=0,qty=0;
      this.rows.forEach((row,index)=>{
        const amount=row.qty*(priceFor(this.config,row.lines.some(v=>v.trim()))+(row.tag !== false && row.tag.trim() ? Number(tagVariant(this.config.giftTag)?.price || 0):0));
        total+=amount;qty+=row.qty;this.querySelector(`[data-row-total="${index}"]`).textContent=this.money(amount);
      });
      this.querySelector('[data-total]').textContent=`${qty} bag${qty===1?'':'s'} · ${this.money(total)}`;
      this.querySelector('[data-submit]').disabled=!this.rows.length || this.busy;
    }
    preview() {
      const revision=++this.previewRevision,row=this.rows[this.active],image=this.querySelector('[data-preview]'),status=this.querySelector('[data-preview-status]');
      if (!row) {image.removeAttribute('src');status.textContent='Add a bag to preview your group.';return;}
      const isTag=this.activeTag && row.tag !== false,config=isTag ? this.config.giftTag:this.config,lines=isTag?[row.tag]:row.lines;
      this.querySelector('[data-preview-label]').textContent=`Previewing ${isTag?'Tag':'Bag'} ${this.active+1}`;
      const src=lines.some(v=>v.trim()) ? previewURL(config,lines,isTag) : config.product.featured_image;
      status.textContent='Loading preview…';
      const next=new Image();next.onload=()=>{if(revision!==this.previewRevision)return;image.src=src;status.textContent='';};next.onerror=()=>{if(revision!==this.previewRevision)return;image.removeAttribute('src');status.textContent='Preview could not load. Your text is saved below; please try focusing the text field again.';};next.src=src;
    }
    confirm() {
      if (this.busy || !this.form.reportValidity()) return;
      try {this.pendingItems=itemsFor(this.config,this.rows);} catch(error){this.error(error.message);return;}
      const summary=this.querySelector('[data-confirm-lines]');summary.replaceChildren();
      this.rows.forEach((row,index)=>{let text=`Bag ${index+1} · Quantity ${row.qty}\n${row.lines.some(v=>v.trim())?row.lines.join('\n'):'Leave blank'}`;if(row.tag!==false&&row.tag.trim())text+='\nLeather tag: '+row.tag;summary.append(this.element('p',text));});
      this.dialog.showModal();
    }
    async addToCart() {
      if(this.busy)return;this.busy=true;this.querySelector('[data-confirm-add]').disabled=true;this.querySelector('[data-edit]').disabled=true;this.totals();
      let sent=false;
      try {
        const items=itemsFor(this.config,this.rows);
        sent=true;
        const response=await fetch(this.root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},credentials:'same-origin',body:JSON.stringify({items})});
        const result=await response.json();
        if(!response.ok){sent=false;throw new Error(typeof result.description==='string'?result.description:'Your group could not be added. Please review availability and quantities.');}
        document.documentElement.dispatchEvent(new CustomEvent('product:added',{bubbles:true,detail:{quantity:items.reduce((sum,item)=>sum+item.quantity,0)}}));
        location.assign(this.root+'cart');
      } catch(error) {
        this.dialog.close();
        if(sent){this.error('We could not confirm the cart response. Check your cart before trying again to avoid adding your bags twice.');const link=this.element('a','View cart',{href:this.root+'cart'});this.status.append(' ',link);}
        else {this.error(error.message);this.busy=false;this.querySelector('[data-confirm-add]').disabled=false;this.querySelector('[data-edit]').disabled=false;this.totals();}
      }
    }
  }
  customElements.define('apolis-group-customizer',GroupCustomizer);
})();
